import { useApolloClient } from '@apollo/client'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { useCallback, useEffect } from 'react'
import { useAppState } from '@/src/common/hooks/useAppState'
import { useLogError } from '@/src/common/hooks/useLogError'
import { graphql } from '@api/mesa'
import { create } from 'zustand'

export const GET_POINTS_BALANCE = graphql(`
  query GetPointsBalance {
    getPointsBalance {
      active {
        value
      }
      pending {
        value
      }
      lifetimeEarned {
        value
      }
      lifetimeSpent {
        value
      }
    }
  }
`)

const initialized = create(() => false)

export function usePoints() {
  const onError = useLogError()

  const query = useLoggedInQuery(GET_POINTS_BALANCE, {
    fetchPolicy: 'cache-and-network',
    onError(...args) {
      initialized.setState(true)
      onError(...args)
    },
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      initialized.setState(true)
    },
  })
  const { isAppVisible } = useAppState()

  const { refetch } = query

  useEffect(
    function updatePointsWheneverAppBecomesVisible() {
      if (isAppVisible) void refetch()
    },
    [isAppVisible, refetch],
  )

  return { query, initialized: initialized() } as const
}

/** will also refresh the points balance when the component/hook unmounts */
export function useRefreshPoints() {
  const client = useApolloClient()

  const refreshPoints = useCallback(
    () => client.refetchQueries({ include: [GET_POINTS_BALANCE] }),
    [client],
  )

  useEffect(
    function refreshPointsOnUnmount() {
      return () => void refreshPoints()
    },
    [refreshPoints],
  )

  return refreshPoints
}
